import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import keys from 'lodash/keys';
import keyBy from 'lodash/keyBy';
import toArray from 'lodash/toArray';
import omit from 'lodash/omit';
import get from 'lodash/get';
import map from 'lodash/map';
import pick from 'lodash/pick';
import forEach from 'lodash/forEach';

import { api, apiV2 } from 'api';
import { combineActions } from '../../utils/combineActions';
import { isChannelHidden } from '../../utils/isChannelHidden';

const initialState = {
  playlists: {},
  favorites: {},
  playlistsKeys: [],
  // An empty playlistsKeys alone cannot mean "this account has no playlists", because it is
  // also empty until the first fetch answers.
  isPlaylistsLoaded: false,
  selectedPlaylist: {
    uuid: null,
    requests: {},
    channels: {},
    categories: [],
    channelsKeys: {},
    /*
     * Lifecycle of the CHANNEL list specifically: 'idle' | 'loading' | 'loaded' | 'error'.
     *
     * The global `isLoading` below cannot answer "are the channels loaded?" — it is shared
     * by several thunks, and fetchPlaylistData.fulfilled clears it. Since the Playlist page
     * fires fetchPlaylistData + fetchChannels together and the former returns a far smaller
     * payload, it resolves first and flips isLoading to false while channels are still in
     * flight. Anything keyed on `!isLoading && isEmpty(keys)` therefore flashes an
     * "empty playlist" state mid-load. Reset to 'idle' by clearSelectedPlaylist.
     */
    channelsStatus: 'idle',
  },
  error: {
    status: null,
    errorKey: null,
  },
  isLoading: false,
  createPlaylistJob: null,
  xtreamValidation: {
    isValidating: false,
    isValid: false,
    serverInfo: null,
    error: null,
    errorKey: null,
  },
};

const actions = {
  fetchPlaylists: createAsyncThunk(
    'playlists/fetchPlaylists',
    () => apiV2.get('playlists'),
  ),
  fetchFavorites: createAsyncThunk(
    'playlists/fetchFavorites',
    () => apiV2.get('playlists/favorites'),
  ),
  fetchPlaylistData: createAsyncThunk(
    'playlists/fetchPlaylistData',
    uuid => apiV2.get(`playlists/${uuid}`)
  ),
  fetchChannels: createAsyncThunk(
    'playlists/fetchChannels',
    (uuid, { rejectWithValue }) => apiV2.get(`playlists/${uuid}/channels`).catch(e => rejectWithValue(e))
  ),
  fetchCategories: createAsyncThunk(
    'playlists/fetchCategories',
    uuid => apiV2.get(`playlists/${uuid}/categories`)
  ),
  createPlaylist: createAsyncThunk(
    'playlists/createPlaylist',
    async ({ data }, { rejectWithValue }) => {
      try {
        return await apiV2.post('playlists/store', data);
      } catch (e) {
        return rejectWithValue(e);
      }
    },
  ),
  editPlaylist: createAsyncThunk(
    'playlists/editPlaylist',
    async ({ uuid, data, deviceUUID }, { rejectWithValue }) => {
      try {
        return await apiV2.post(`playlists/${uuid}`, data, {
          headers: {
            'Device-UUID': deviceUUID,
          },
        });
      } catch (e) {
        return rejectWithValue(e);
      }
    },
  ),
  changeVisibility: createAsyncThunk(
    'playlists/changeVisibility',
    ({ uuid, data, deviceUUID }) => apiV2.post(`playlists/${uuid}/visibility`, data, {
      headers: {
        'Device-UUID': deviceUUID,
      },
    }),
  ),
  syncPlaylist: createAsyncThunk(
    'playlists/syncPlaylist',
    async ({ uuid: playlistUUID }, { rejectWithValue }) => {
      try {
        return await apiV2.post(`playlists/${playlistUUID}/merge`, { forced: true });
      } catch (e) {
        return rejectWithValue(e);
      }
    }
  ),
  deletePlaylist: createAsyncThunk(
    'playlists/deletePlaylist',
    ({ uuid }) => apiV2.delete(`playlists/${uuid}`)
  ),
  hideChannel: createAsyncThunk(
    'playlists/hideChannel',
    ({ playlistUUID, channelUUID, hidden }) =>
      apiV2.post(`playlists/${playlistUUID}/channels/${channelUUID}/hide`, { hidden }),
  ),
  updateFavorite: createAsyncThunk(
    'playlists/updateFavorite',
    ({ playlistUUID, channelUUID, favorite }) =>
      apiV2.post(`playlists/${playlistUUID}/channels/${channelUUID}/favorite`, { favorite })
  ),
  reorderChannels: createAsyncThunk(
    'playlists/reorderChannels',
    ({ playlistUUID, oldIndex, newIndex }) =>
      apiV2.post(`playlists/${playlistUUID}/channels/reorder`,
        { 'old_index': oldIndex, 'new_index': newIndex }
      ),
  ),
  reorderFavorites: createAsyncThunk(
    'playlists/reorderFavorites',
    ({ oldIndex, newIndex }) =>
      apiV2.post(`playlists/channels/reorder`,
        { 'old_index': oldIndex, 'new_index': newIndex }
      ),
  ),
  toggleCategoryVisibility: createAsyncThunk(
    'playlists/toggleCategoryVisibility',
    ({ playlistUUID, groupId, action }) =>
      apiV2.post(`playlists/${playlistUUID}/categories/${groupId}/visibility`, { action }),
  ),
  checkJobStatus: createAsyncThunk(
    'jobs/checkJobStatus', jobID => api.get(`jobs/${jobID}`, { unwrap: true }),
  ),
  updatePlaylistOrderType: createAsyncThunk(
    'playlists/updatePlaylistOrderType',
    ({ uuid, orderType }) => apiV2.post(`playlists/${uuid}/order-type`, { order_type: orderType })
  ),
  // Xtream Codes actions
  validateXtreamCredentials: createAsyncThunk(
    'playlists/validateXtreamCredentials',
    async ({ xc_server_url, xc_username, xc_password }, { rejectWithValue }) => {
      try {
        return await apiV2.post('playlists/xtream/validate', {
          xc_server_url,
          xc_username,
          xc_password
        });
      } catch (e) {
        return rejectWithValue(e);
      }
    }
  ),
  createXtreamPlaylist: createAsyncThunk(
    'playlists/createXtreamPlaylist',
    async ({ name, xc_server_url, xc_username, xc_password }, { rejectWithValue }) => {
      try {
        return await apiV2.post('playlists/xtream', {
          name,
          xc_server_url,
          xc_username,
          xc_password
        });
      } catch (e) {
        return rejectWithValue(e);
      }
    }
  ),
  updateXtreamPlaylist: createAsyncThunk(
    'playlists/updateXtreamPlaylist',
    async ({ uuid, data }, { rejectWithValue }) => {
      try {
        /*
         * `unwrap` because this endpoint returns { data: PlaylistResource, job_id, … } and the
         * default interceptor yields `response.data.data` — which silently drops job_id. The
         * backend queues an XC merge whenever credentials change, so without job_id the modal
         * had nothing to poll and closed over a job that had not run yet.
         *
         * Flattened back to "resource + job_id" so the fulfilled reducer keeps receiving a
         * playlist shaped object (it reads payload.uuid).
         */
        const envelope = await apiV2.post(`playlists/${uuid}/xtream`, data, { unwrap: true });
        return { ...envelope.data, job_id: envelope.job_id ?? null };
      } catch (e) {
        return rejectWithValue(e);
      }
    }
  ),
  syncXtreamPlaylist: createAsyncThunk(
    'playlists/syncXtreamPlaylist',
    async ({ uuid }, { rejectWithValue }) => {
      try {
        return await apiV2.post(`playlists/${uuid}/xtream/sync`);
      } catch (e) {
        return rejectWithValue(e);
      }
    }
  ),
  getXtreamServerInfo: createAsyncThunk(
    'playlists/getXtreamServerInfo',
    async ({ uuid }, { rejectWithValue }) => {
      try {
        return await apiV2.get(`playlists/${uuid}/xtream/info`);
      } catch (e) {
        return rejectWithValue(e);
      }
    }
  ),
};

const slice = createSlice({
  name: 'playlists',
  initialState,
  reducers: {
    selectPlaylist: (state, { payload }) => {
      state.selectedPlaylist = { ...initialState.selectedPlaylist, uuid: payload };
    },
    clearSelectedPlaylist: (state => {
      state.selectedPlaylist = initialState.selectedPlaylist;
    }),
    clearModal: (state => {
      state.error.status = null;
      state.error.errorKey = null;
      state.createPlaylistJob = null;
      state.xtreamValidation = initialState.xtreamValidation;
    }),
    resetXtreamValidation: (state => {
      state.xtreamValidation = initialState.xtreamValidation;
    }),
    setLoader: (state, { payload }) => {
      if (state.playlists[payload.uuid]) state.playlists[payload.uuid].isLoading = payload.isLoading;
    },
    clear: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(actions.fetchPlaylists.fulfilled, (state, { payload }) => {
        state.playlists = keyBy(payload, 'uuid');
        state.playlistsKeys = keys(keyBy(payload, 'uuid'));
        state.isPlaylistsLoaded = true;
      })
      .addCase(actions.fetchFavorites.fulfilled, (state, { payload }) => {
        state.favorites = keyBy(payload, 'uuid');
      })
      .addCase(actions.fetchPlaylistData.pending, (state, { meta }) => {
        if (state.selectedPlaylist.uuid === meta.arg) state.selectedPlaylist.requests.details = meta.requestId;
      })
      .addCase(actions.fetchPlaylistData.fulfilled, (state, { payload, meta }) => {
        if (state.playlists[payload.uuid]) state.playlists[payload.uuid].channels_count = payload.channels_count;
        if (state.selectedPlaylist.uuid === meta.arg && state.selectedPlaylist.requests.details === meta.requestId) {
          state.selectedPlaylist.devices = payload.devices;
        }
      })
      .addCase(actions.fetchChannels.fulfilled, (state, { payload, meta }) => {
        if (state.selectedPlaylist.uuid !== meta.arg || state.selectedPlaylist.requests.channels !== meta.requestId) return;
        state.selectedPlaylist.channelsStatus = 'loaded';
        state.selectedPlaylist.channels = keyBy(payload, 'hash');
        state.selectedPlaylist.channelsKeys = keyBy(
          map(payload, (channel) =>
            pick(channel, [
              'uuid',
              'favorite',
              'hash',
              'hidden',
              'sort_order',
              'playlist_group_id',
            ])
          ),
          'hash'
        );
      })
      .addCase(actions.fetchCategories.pending, (state, { meta }) => {
        if (state.selectedPlaylist.uuid === meta.arg) state.selectedPlaylist.requests.categories = meta.requestId;
      })
      .addCase(actions.fetchCategories.fulfilled, (state, { payload, meta }) => {
        if (state.selectedPlaylist.uuid !== meta.arg || state.selectedPlaylist.requests.categories !== meta.requestId) return;
        state.selectedPlaylist.categories = payload;
      })
      /*
       * The API answers a drag with ONLY the positions that moved — the inclusive
       * [min(old,new), max(old,new)] window — not the whole playlist. It used to return
       * every channel, which on a 30k playlist was a ~20MB response the backend OOM'd
       * building (see core PlaylistOrderService::reorderChannels).
       *
       * So this merges rather than replaces. Everything outside the window keeps the
       * sort_order already in the store, and `channels` keeps the names/thumbnails that
       * a positions-only payload could never carry.
       */
      .addCase(actions.reorderChannels.fulfilled, (state, { payload, meta: { arg } }) => {
        if (state.playlists[arg.playlistUUID]) state.playlists[arg.playlistUUID].order_type = 'custom';
        if (state.selectedPlaylist.uuid !== arg.playlistUUID) return;

        const { channels, channelsKeys } = state.selectedPlaylist;

        forEach(payload.channels, ({ hash, sort_order: sortOrder }) => {
          if (channelsKeys[hash]) channelsKeys[hash].sort_order = sortOrder;
          if (channels[hash]) channels[hash].sort_order = sortOrder;
        });

        /*
         * Display order IS key insertion order (see the `channelsKeys` selector), so the
         * map has to be rebuilt for the move to show. Hidden channels are excluded from
         * every list view and all share sort_order 0, so they keep their relative order
         * instead of joining the sort and scrambling the Hidden filter.
         */
        const entries = toArray(channelsKeys);
        const reordered = {};

        entries
          .filter((channel) => !isChannelHidden(channel))
          // Coerced: this merge deliberately leaves untouched entries with whatever
          // sort_order the store already had, and `sort_order` is only picked from the API,
          // never defaulted. A missing one makes `a - b` NaN, which the spec coerces to "equal
          // to everything" — that is not a total order, so V8 may emit an arbitrary
          // permutation and silently scramble the whole list after a single drag.
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
          .concat(entries.filter(isChannelHidden))
          .forEach((channel) => {
            reordered[channel.hash] = { ...channel };
          });

        state.selectedPlaylist.channelsKeys = reordered;
      })
      // todo: combine actions here
      .addCase(actions.createPlaylist.pending, (state) => {
        state.error.status = null;
        state.error.errorKey = null;
      })
      .addCase(actions.createPlaylist.fulfilled, (state, { payload }) => {
        if (payload.job_id) {
          // Job-based response - don't add to playlists list yet, wait for job completion
          // Just store the job info for tracking, playlist will be added when job completes
          state.createPlaylistJob = {
            uuid: payload.uuid,
            jobId: payload.job_id,
            status: 'pending'
          };
        } else {
          // Direct response - traditional handling
          state.playlists[payload.uuid] = omit(payload, ['channels']);
          state.playlistsKeys.push(payload.uuid);
        }
        // todo add channels if needed
      })
      .addCase(actions.createPlaylist.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error.status = payload?.status || 500;
        state.error.errorKey = payload?.data?.i18n_key || null;
      })
      .addCase(actions.editPlaylist.pending, (state) => {
        state.error.status = null;
        state.error.errorKey = null;
      })
      .addCase(actions.editPlaylist.fulfilled, (state, { payload }) => {
        state.playlists[payload.uuid] = omit(payload, ['channels']);
        if (state.selectedPlaylist.uuid !== payload.uuid) return;
        state.selectedPlaylist.devices = get(payload, 'devices', []);
        state.selectedPlaylist.channels = keyBy(payload.channels, 'hash');
        state.selectedPlaylist.channelsKeys = keyBy(
          map(payload.channels, (channel) =>
            pick(channel, [
              'uuid',
              'favorite',
              'hash',
              'hidden',
              'sort_order',
              'playlist_group_id',
            ])
          ),
          'hash'
        );
        state.selectedPlaylist.categories = payload.categories;
      })
      .addCase(actions.changeVisibility.fulfilled, (state, { payload }) => {
        if (state.playlists[payload.uuid]) state.playlists[payload.uuid].devices = payload.devices;
        if (state.selectedPlaylist.uuid === payload.uuid) state.selectedPlaylist.devices = payload.devices;
      })
      // Was hardcoded to 400, so an edit failure could never surface the file-specific copy
      // (which keys off 404) or an API i18n_key.
      .addCase(actions.editPlaylist.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error.status = payload?.status || 400;
        state.error.errorKey = payload?.data?.i18n_key || null;
      })
      .addCase(actions.syncPlaylist.fulfilled, (state, { payload }) => {
        state.playlists[payload.uuid].job = { status: 'pending', id: payload.job_id };
      })
      .addCase(actions.syncPlaylist.rejected, (state, { meta: { arg } }) => {
        state.playlists[arg.uuid].isLoading = false;
      })
      .addCase(actions.deletePlaylist.fulfilled, (state, { meta: { arg } }) => {
        const index = state.playlistsKeys.findIndex((i) => i === arg.uuid);
        if (index !== -1) state.playlistsKeys.splice(index, 1);
        delete state.playlists[arg.uuid];
      })
      .addCase(actions.updateFavorite.fulfilled, (state, { meta: { arg } }) => {
        if (state.selectedPlaylist.uuid === arg.playlistUUID && state.selectedPlaylist.channels[arg.channelUUID]) {
          state.selectedPlaylist.channels[arg.channelUUID].favorite = arg.favorite;
        }
        if (arg.favorite === 1) {
          state.favorites[arg.channelUUID] = {};
        } else {
          delete state.favorites[arg.channelUUID];
        }
      })
      .addCase(actions.hideChannel.fulfilled, (state, { meta: { arg } }) => {
        if (state.selectedPlaylist.uuid === arg.playlistUUID) {
          const key = state.selectedPlaylist.channelsKeys[arg.channelUUID];
          const channel = state.selectedPlaylist.channels[arg.channelUUID];
          if (key) { key.hidden = arg.hidden; key.favorite = 0; }
          if (channel) { channel.hidden = arg.hidden; channel.favorite = 0; }
        }
        delete state.favorites[arg.channelUUID];
      })
      .addCase(actions.toggleCategoryVisibility.fulfilled, (state, { payload, meta }) => {
        payload.forEach((channel) => {
          if (!channel.favorite || isChannelHidden(channel)) delete state.favorites[channel.uuid];
        });
        if (state.selectedPlaylist.uuid !== meta.arg.playlistUUID) return;
        state.selectedPlaylist.channelsKeys = keyBy(payload, 'uuid');
        state.selectedPlaylist.channels = keyBy(payload, 'uuid');
      })
      .addCase(actions.checkJobStatus.fulfilled, (state, { payload, meta }) => {
        const isCreateJob = state.createPlaylistJob?.jobId === meta.arg;
        // Handle job status updates
        if (payload.uuid && state.playlists[payload.uuid]?.job?.id === meta.arg) {
          state.playlists[payload.uuid].job.status = payload.status;
        }
        // For createPlaylist jobs, update the job status in createPlaylistJob
        if (isCreateJob) {
          state.createPlaylistJob.status = payload.status;
        }

        if (!isCreateJob) return;

        // Handle failed/aborted jobs - set error status
        if (payload.status === 'failed' || payload.status === 'aborted') {
          state.error.status = payload.status;
          state.error.errorKey = payload.data?.i18n_key || null;
          // Clear the createPlaylistJob since it failed
          if (state.createPlaylistJob) {
            state.createPlaylistJob = null;
          }
        }
        // Handle job failure indicated by 'reason' without job metadata (fallback)
        else if (payload.reason && !payload.user_id && !payload.request_data) {
          state.error.status = 'aborted';
          state.error.errorKey = null;
          // Clear the createPlaylistJob since it failed
          if (state.createPlaylistJob) {
            state.createPlaylistJob = null;
          }
        }
        // Handle completed jobs - add playlist to main list
        else if (payload.status === 'completed' && payload.data?.playlist_uuid && state.createPlaylistJob) {
          const playlist = {
            uuid: payload.data.playlist_uuid,
            id: payload.data.playlist_id,
            channels_count: payload.data.channels_count,
            name: state.createPlaylistJob.name || 'New Playlist',
            isLoading: false
          };
          state.playlists[payload.data.playlist_uuid] = playlist;
          state.playlistsKeys.push(payload.data.playlist_uuid);
          state.createPlaylistJob = null;
        }
      })
      .addCase(actions.updatePlaylistOrderType.fulfilled, (state, { payload, meta: { arg } }) => {
        // Update the entire playlist object from the response
        state.playlists[arg.uuid] = { ...state.playlists[arg.uuid], ...payload };
      })
      // Xtream Codes validation
      .addCase(actions.validateXtreamCredentials.pending, (state) => {
        state.xtreamValidation.isValidating = true;
        state.xtreamValidation.isValid = false;
        state.xtreamValidation.error = null;
        state.xtreamValidation.errorKey = null;
        state.xtreamValidation.serverInfo = null;
        state.error.status = null;
        state.error.errorKey = null;
      })
      .addCase(actions.validateXtreamCredentials.fulfilled, (state, { payload }) => {
        state.xtreamValidation.isValidating = false;
        // Interceptor unwraps to: { valid, server_info: {...}, user_info: {...} }
        state.xtreamValidation.isValid = payload?.valid || false;
        state.xtreamValidation.serverInfo = payload || null;
        state.xtreamValidation.error = null;
      })
      .addCase(actions.validateXtreamCredentials.rejected, (state, { payload }) => {
        state.xtreamValidation.isValidating = false;
        state.xtreamValidation.isValid = false;
        state.xtreamValidation.serverInfo = null;
        // 422 error: invalid credentials or validation errors
        // 503 error: server connection failed
        // Prefer the server's i18n_key (localized in the component); fall back to
        // the raw server message so RU users don't see an English string.
        state.xtreamValidation.error = payload?.data?.message || payload?.message || 'Validation failed';
        state.xtreamValidation.errorKey = payload?.data?.i18n_key || null;
        // Don't set global error for validation - it has its own error display
        // state.error.status and state.error.errorKey should only be set for playlist create/edit errors
      })
      // Xtream Codes create playlist
      .addCase(actions.createXtreamPlaylist.pending, (state) => {
        state.error.status = null;
        state.error.errorKey = null;
        state.isLoading = true;
      })
      .addCase(actions.createXtreamPlaylist.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        if (payload.job_id) {
          state.createPlaylistJob = {
            jobId: payload.job_id,
            status: 'pending'
          };
        }
      })
      .addCase(actions.createXtreamPlaylist.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error.status = payload?.status || 500;
        state.error.errorKey = payload?.data?.i18n_key || null;
      })
      // Xtream Codes update (rename / credential change)
      .addCase(actions.updateXtreamPlaylist.pending, (state) => {
        state.error.status = null;
        state.error.errorKey = null;
      })
      .addCase(actions.updateXtreamPlaylist.fulfilled, (state, { payload }) => {
        if (payload?.uuid) {
          // job_id is transport, not playlist state — the caller reads it off the payload.
          state.playlists[payload.uuid] = { ...state.playlists[payload.uuid], ...omit(payload, ['channels', 'job_id']) };
        }
      })
      .addCase(actions.updateXtreamPlaylist.rejected, (state, { payload }) => {
        state.error.status = payload?.status || 500;
        state.error.errorKey = payload?.data?.i18n_key || null;
      })
      // Xtream Codes sync
      .addCase(actions.syncXtreamPlaylist.pending, (state, { meta: { arg } }) => {
        if (state.playlists[arg.uuid]) {
          state.playlists[arg.uuid].isLoading = true;
        }
      })
      .addCase(actions.syncXtreamPlaylist.fulfilled, (state, { payload, meta: { arg } }) => {
        if (state.playlists[arg.uuid] && payload.job_id) {
          state.playlists[arg.uuid].job = { status: 'pending', id: payload.job_id };
        }
      })
      .addCase(actions.syncXtreamPlaylist.rejected, (state, { meta: { arg } }) => {
        if (state.playlists[arg.uuid]) {
          state.playlists[arg.uuid].isLoading = false;
        }
      })
      .addMatcher(
        combineActions(
          actions.fetchChannels.pending,
        ),
        (state, { meta }) => {
          if (state.selectedPlaylist.uuid !== meta.arg) return;
          state.isLoading = true;
          state.selectedPlaylist.channelsStatus = 'loading';
          state.selectedPlaylist.requests.channels = meta.requestId;
        }
      )
      .addMatcher(
        combineActions(
          actions.fetchChannels.rejected,
        ),
        (state, { meta }) => {
          if (state.selectedPlaylist.uuid !== meta.arg || state.selectedPlaylist.requests.channels !== meta.requestId) return;
          state.selectedPlaylist.channelsStatus = 'error';
        }
      )
      .addMatcher(
        combineActions(
          actions.fetchPlaylistData.fulfilled,
          actions.fetchPlaylistData.rejected,
          actions.fetchChannels.fulfilled,
          actions.fetchChannels.rejected,
        ),
        (state) => {
          state.isLoading = false;
        }
      )
      .addMatcher(
        combineActions(
          actions.syncPlaylist.pending,
        ),
        (state, { meta: { arg } }) => {
          state.playlists[arg.uuid].isLoading = true;
        }
      )
      .addMatcher(
        combineActions(
          actions.createPlaylist.pending,
        ),
        (state) => {
          state.isLoading = true;
        }
      )
      .addMatcher(
        combineActions(
          actions.createPlaylist.fulfilled,
        ),
        (state) => {
          state.isLoading = false;
        }
      )
  },
});

export const playlists = {
  reducer: slice.reducer,
  actions: {
    ...slice.actions,
    ...actions,
  },
  selectors: {
    playlists: createSelector( // please don't use it. READ_ONLY
      state => state.playlists.playlists,
      playlists => playlists,
    ),
    isPlaylistsLoaded: state => state.playlists.isPlaylistsLoaded,
    keys: createSelector(
      state => state.playlists.playlistsKeys,
      keys => [...keys].reverse(),
    ),
    makePlaylistSelector: () => createSelector(
      (state, uuid) => state.playlists.playlists[uuid],
      playlist => playlist,
    ),
    favorites: createSelector(
      state => state.playlists.favorites,
      favorites => toArray(favorites),
    ),
    hasHiddenChannels: createSelector(
      state => state.playlists.selectedPlaylist.channelsKeys,
      channels => toArray(channels).find(({ hidden }) => hidden ),
    ),
    channelsKeys: createSelector(
      state => state.playlists.selectedPlaylist.channelsKeys,
      keys => toArray(keys),
    ),
    // A plain accessor: the value is a string, so a memoised selector could never save work.
    channelsStatus: state => state.playlists.selectedPlaylist.channelsStatus,
    makeChannelSelector: () => createSelector(
      (state, uuid) => state.playlists.selectedPlaylist.channels[uuid],
      channel => channel,
    ),
    playlistCategories: createSelector(
      state => state.playlists.selectedPlaylist.categories,
      categories => categories,
    ),
    playlistDevices: createSelector(
      state => state.playlists.selectedPlaylist.devices,
      devices => devices || [],
    ),
    errorStatus: createSelector(
      state => state.playlists.error.status,
      errorStatus => errorStatus,
    ),
    errorKey: createSelector(
      state => state.playlists.error.errorKey,
      errorKey => errorKey,
    ),
    isLoading: createSelector(
      state => state.playlists.isLoading,
      isLoading => isLoading,
    ),
    job: createSelector(
      state => state.playlists.selectedPlaylist.job,
      job => job,
    ),
    xtreamValidation: createSelector(
      state => state.playlists.xtreamValidation,
      validation => validation,
    ),
  }
};
